// React Query setup for aggressive caching and background updates
// Africa-optimized with network-aware retry logic
import { QueryClient } from '@tanstack/react-query';

/**
 * Detect network quality for adaptive retry logic
 */
function getNetworkQuality() {
  const connection = (navigator as any).connection 
    || (navigator as any).mozConnection 
    || (navigator as any).webkitConnection;
  
  if (connection) {
    const type = connection.effectiveType || 'unknown';
    return {
      type,
      isSlow: ['slow-2g', '2g', '3g'].includes(type),
    };
  }
  
  return { type: 'unknown', isSlow: false };
}

/** PostgREST / HTTP errors that will fail the same way on every retry. */
const PERMANENT_PG_CODES = new Set(['42501', '42P01', '42703', 'PGRST116', 'PGRST301', 'PGRST202', 'PGRST204']);

export function isPermanentQueryError(error: any): boolean {
  if (!error) return false;
  const status = Number(error.status ?? error.statusCode);
  if (status >= 400 && status < 500 && status !== 408 && status !== 429) return true;
  return typeof error.code === 'string' && PERMANENT_PG_CODES.has(error.code);
}


export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache for 10 minutes for better performance
      staleTime: 10 * 60 * 1000,
      // Keep in cache for 1 hour for better UX
      gcTime: 60 * 60 * 1000,
      // Network-aware retry logic for Africa
      retry: (failureCount, error: any) => {
        if (isPermanentQueryError(error)) {
          return false;
        }
        
        // On slow connections, reduce retries
        const { isSlow } = getNetworkQuality();
        const maxRetries = isSlow ? 2 : 3;
        
        return failureCount < maxRetries;
      },
      // Network-aware retry delays
      retryDelay: (attemptIndex) => {
        const { isSlow } = getNetworkQuality();
        // Slower connections: longer delays between retries
        const baseDelay = isSlow ? 2000 : 1000;
        return Math.min(baseDelay * 2 ** attemptIndex, 30000);
      },
      // Refetch on focus only when data is stale (respects staleTime);
      // never on slow connections.
      refetchOnWindowFocus: () => {
        const { isSlow } = getNetworkQuality();
        return !isSlow;
      },
      refetchOnReconnect: 'always',
      // Background refetch only for critical data
      refetchInterval: false, // Disabled by default, enabled per-query
      // Network mode for better offline experience
      networkMode: 'online',
    },
    mutations: {
      // Retry mutations on network errors
      retry: 2,
      retryDelay: 1000,
    },
  },
});

// Cache keys for consistent invalidation
export const CACHE_KEYS = {
  trials: 'trials',
  trialDetail: 'trial-detail',
  userMatches: 'user-matches',
  directoryListings: 'directory-listings',
  userProfile: 'user-profile',
  blogPosts: 'blog-posts',
  trialSites: 'trial-sites',
  userAdminStatus: 'user-admin-status',
} as const;
